import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import OpportunityDocuments from './OpportunityDocuments';
import ActivityLogger from '../utils/activityLogger';
import ActivityTimeline from './ActivityTimeline';
import AIRelanceGenerator from './AIRelanceGenerator';
import Pagination from './Pagination';
import './css/OpportunityCard.css';
import './css/ToolbarUX.css';
import './css/BaobabDesignSystem.css';
import './css/CompactForms.css';

const OpportunitiesModule = () => {
  const [opportunities, setOpportunities] = useState([]);
  const [societes, setSocietes] = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateSocieteModal, setShowCreateSocieteModal] = useState(false);
  const [showOperationsModal, setShowOperationsModal] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [opportunityOperations, setOpportunityOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [activeTab, setActiveTab] = useState('opportunities'); // 'opportunities' or 'history'
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [commercialFilter, setCommercialFilter] = useState('');
  const [sortBy, setSortBy] = useState('date_creation');
  const [sortOrder, setSortOrder] = useState('desc');
  const [exportLoading, setExportLoading] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  // Selection multiple states
  const [selectedOpportunities, setSelectedOpportunities] = useState([]);
  const [showBatchActionsModal, setShowBatchActionsModal] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  
  // New states for view and history modals
  const [showViewModal, setShowViewModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  // AI Relance functionality
  const [showAIRelanceModal, setShowAIRelanceModal] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && !event.target.closest('.baobab-dropdown') && !event.target.closest('.group\\/menu')) {
        setActiveDropdown(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeDropdown]);

  const [formData, setFormData] = useState({
    numero_opportunite: '',
    nom_opportunite: '',
    description: '',
    client_id: '',
    client_name: '',
    commercial_responsable: '',
    statut: 'prospection',
    valeur_estimee: 0,
    probabilite: 50,
    date_cloture_prevue: ''
  });

  const [societeFormData, setSocieteFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    rccm: '',
    contact_person: '',
    industry: '',
    status: 'active',
    notes: '',
    contact_facturation: {
      nom: '',
      email: '',
      telephone: ''
    },
    contact_livraison: {
      nom: '',
      email: '',
      telephone: '',
      adresse: ''
    },
    contact_notification: {
      nom: '',
      email: '',
      telephone: ''
    }
  });

  const { user } = useAuth();

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    fetchOpportunities();
    fetchSocietes();
    fetchCommerciaux();
    // Auto-fill commercial responsable with current user info
    if (user) {
      setFormData(prev => ({
        ...prev,
        commercial_responsable: `${user.full_name} (${user.email})`
      }));
    }
  }, [user]);

  const fetchOpportunities = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/opportunities`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOpportunities(data);
      }
    } catch (error) {
      console.error('Error fetching opportunities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/export/opportunities/csv`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'opportunities_export.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Erreur lors de l\'export CSV');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'export CSV');
    } finally {
      setExportLoading(false);
    }
  };

  const fetchSocietes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/crm/clients`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Societes fetched:', data); // Debug
        setSocietes(data);
      } else {
        console.error('Error fetching societes:', response.status, response.statusText);
        // Fallback: essayer l'ancien endpoint si le nouveau échoue
        const fallbackResponse = await fetch(`${API_BASE_URL}/api/societes`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          console.log('Societes fetched from fallback:', fallbackData); // Debug
          setSocietes(fallbackData);
        }
      }
    } catch (error) {
      console.error('Error fetching societes:', error);
      // Essayer encore l'ancien endpoint en cas d'erreur réseau
      try {
        const token = localStorage.getItem('token');
        const fallbackResponse = await fetch(`${API_BASE_URL}/api/societes`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          console.log('Societes fetched from fallback after error:', fallbackData); // Debug
          setSocietes(fallbackData);
        }
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }
    }
  };

  const fetchCommerciaux = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/commerciaux`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCommerciaux(data);
      }
    } catch (error) {
      console.error('Error fetching commerciaux:', error);
    }
  };

  const fetchOpportunityOperations = async (opportunityId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/opportunities/${opportunityId}/operations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOpportunityOperations(data);
      } else {
        // Fallback: fetch all operations and filter by opportunity
        const allOpsResponse = await fetch(`${API_BASE_URL}/api/operations`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (allOpsResponse.ok) {
          const allOps = await allOpsResponse.json();
          const filteredOps = allOps.filter(op => op.opportunite_id === opportunityId);
          setOpportunityOperations(filteredOps);
        }
      }
    } catch (error) {
      console.error('Error fetching opportunity operations:', error);
      setOpportunityOperations([]);
    }
  };

  const handleViewOperations = async (opportunity) => {
    setSelectedOpportunity(opportunity);
    await fetchOpportunityOperations(opportunity.id);
    setShowOperationsModal(true);
  };

  const handleViewDocuments = (opportunity) => {
    setSelectedOpportunity(opportunity);
    setShowDocumentsModal(true);
  };

  const handleEditOpportunity = (opportunity) => {
    setSelectedOpportunity(opportunity);
    setFormData({
      numero_opportunite: opportunity.numero_opportunite,
      nom_opportunite: opportunity.nom_opportunite,
      description: opportunity.description,
      client_id: opportunity.client_id,
      client_name: opportunity.client_name,
      commercial_responsable: opportunity.commercial_responsable,
      statut: opportunity.statut,
      valeur_estimee: opportunity.valeur_estimee,
      probabilite: opportunity.probabilite,
      date_cloture_prevue: opportunity.date_cloture_prevue
    });
    setIsEditing(true);
    setShowEditModal(true);
  };

  const handleDuplicateOpportunity = async (opportunity) => {
    try {
      const token = localStorage.getItem('token');
      
      // Créer un nouveau numéro d'opportunité
      const newNumero = `OPP-${Date.now()}`;
      const duplicatedData = {
        numero_opportunite: newNumero,
        nom_opportunite: `${opportunity.nom_opportunite} (Copie)`,
        description: opportunity.description,
        client_id: opportunity.client_id,
        client_name: opportunity.client_name,
        commercial_responsable: opportunity.commercial_responsable,
        statut: 'prospection', // Remettre en prospection
        valeur_estimee: opportunity.valeur_estimee,
        probabilite: opportunity.probabilite,
        date_cloture_prevue: opportunity.date_cloture_prevue
      };
      
      const response = await fetch(`${API_BASE_URL}/api/opportunities`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(duplicatedData)
      });
      
      if (response.ok) {
        const newOpportunity = await response.json();
        
        // Log the duplication activity
        try {
          await ActivityLogger.opportunityCreated(user, newOpportunity, ['duplication']);
        } catch (logError) {
          console.warn('Failed to log opportunity duplication:', logError);
        }
        
        await fetchOpportunities();
        alert('Opportunité dupliquée avec succès !');
        
        // Ouvrir directement en édition pour permettre les modifications rapides
        setTimeout(() => {
          handleEditOpportunity(newOpportunity);
        }, 500);
      } else {
        console.error('Error duplicating opportunity');
        alert('Erreur lors de la duplication');
      }
    } catch (error) {
      console.error('Error duplicating opportunity:', error);
      alert('Erreur lors de la duplication');
    }
  };

  const handleUpdateOpportunity = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/opportunities/${selectedOpportunity.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        const updatedOpportunity = await response.json();
        
        // Log the opportunity update activity
        try {
          await ActivityLogger.opportunityUpdated(user, updatedOpportunity, ['general_update']);
        } catch (logError) {
          console.warn('Failed to log opportunity update:', logError);
        }
        
        await fetchOpportunities();
        setShowEditModal(false);
        setIsEditing(false);
        resetForm();
        setSelectedOpportunity(null);
      }
    } catch (error) {
      console.error('Error updating opportunity:', error);
    }
  };

  const handleDeleteOpportunity = (opportunity) => {
    setSelectedOpportunity(opportunity);
    setShowDeleteModal(true);
  };

  const confirmDeleteOpportunity = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/opportunities/${selectedOpportunity.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        // Log the delete activity before fetching opportunities
        try {
          await ActivityLogger.opportunityUpdated(user, selectedOpportunity, ['suppression']);
        } catch (logError) {
          console.warn('Failed to log opportunity deletion:', logError);
        }
        
        await fetchOpportunities();
        setShowDeleteModal(false);
        setSelectedOpportunity(null);
      }
    } catch (error) {
      console.error('Error deleting opportunity:', error);
    }
  };

  const handleArchiveOpportunity = async (opportunity) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/opportunities/${opportunity.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...opportunity,
          statut: 'archivée'
        })
      });
      
      if (response.ok) {
        const updatedOpportunity = await response.json();
        
        // Log the archive activity
        try {
          await ActivityLogger.opportunityStatusChanged(user, updatedOpportunity, opportunity.statut, 'archivée');
        } catch (logError) {
          console.warn('Failed to log opportunity archive:', logError);
        }
        
        await fetchOpportunities();
      }
    } catch (error) {
      console.error('Error archiving opportunity:', error);
    }
  };

  const handleCreateOpportunity = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/opportunities`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        const newOpportunity = await response.json();
        
        // Log opportunity creation activity
        try {
          await ActivityLogger.opportunityCreated(user, newOpportunity);
        } catch (logError) {
          console.warn('Failed to log opportunity creation:', logError);
        }
        
        await fetchOpportunities();
        setShowCreateModal(false);
        resetForm();
      }
    } catch (error) {
      console.error('Error creating opportunity:', error);
    }
  };

  const handleCreateSociete = async (e) => {
    e.preventDefault();
    
    try {
      console.log('📝 Création société - Données brutes:', societeFormData);
      
      // Nettoyer les données avant envoi - ne pas envoyer les contacts vides
      const cleanedData = { ...societeFormData };
      
      // Contact Facturation : supprimer complètement si vide, sinon nettoyer les champs vides
      if (!societeFormData.contact_facturation?.nom && !societeFormData.contact_facturation?.email && !societeFormData.contact_facturation?.telephone) {
        delete cleanedData.contact_facturation;
      } else if (societeFormData.contact_facturation) {
        cleanedData.contact_facturation = {};
        if (societeFormData.contact_facturation.nom) cleanedData.contact_facturation.nom = societeFormData.contact_facturation.nom;
        if (societeFormData.contact_facturation.email) cleanedData.contact_facturation.email = societeFormData.contact_facturation.email;
        if (societeFormData.contact_facturation.telephone) cleanedData.contact_facturation.telephone = societeFormData.contact_facturation.telephone;
      }
      
      // Contact Livraison : supprimer complètement si vide, sinon nettoyer les champs vides
      if (!societeFormData.contact_livraison?.nom && !societeFormData.contact_livraison?.email && !societeFormData.contact_livraison?.telephone && !societeFormData.contact_livraison?.adresse) {
        delete cleanedData.contact_livraison;
      } else if (societeFormData.contact_livraison) {
        cleanedData.contact_livraison = {};
        if (societeFormData.contact_livraison.nom) cleanedData.contact_livraison.nom = societeFormData.contact_livraison.nom;
        if (societeFormData.contact_livraison.email) cleanedData.contact_livraison.email = societeFormData.contact_livraison.email;
        if (societeFormData.contact_livraison.telephone) cleanedData.contact_livraison.telephone = societeFormData.contact_livraison.telephone;
        if (societeFormData.contact_livraison.adresse) cleanedData.contact_livraison.adresse = societeFormData.contact_livraison.adresse;
      }
      
      // Contact Notification : supprimer complètement si vide, sinon nettoyer les champs vides
      if (!societeFormData.contact_notification?.nom && !societeFormData.contact_notification?.email && !societeFormData.contact_notification?.telephone) {
        delete cleanedData.contact_notification;
      } else if (societeFormData.contact_notification) {
        cleanedData.contact_notification = {};
        if (societeFormData.contact_notification.nom) cleanedData.contact_notification.nom = societeFormData.contact_notification.nom;
        if (societeFormData.contact_notification.email) cleanedData.contact_notification.email = societeFormData.contact_notification.email;
        if (societeFormData.contact_notification.telephone) cleanedData.contact_notification.telephone = societeFormData.contact_notification.telephone;
      }
      
      console.log('📝 Création société - Données nettoyées:', cleanedData);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/crm/clients`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(cleanedData)
      });
      
      console.log('📡 Réponse API:', response.status);
      
      if (response.ok) {
        const newSociete = await response.json();
        console.log('✅ Société créée:', newSociete);
        await fetchSocietes();
        
        // Auto-select the new company in the opportunity form
        setFormData({
          ...formData,
          client_id: newSociete.id,
          client_name: newSociete.name,
          commercial_responsable: newSociete.responsable_compte_nom || ''
        });
        
        setShowCreateSocieteModal(false);
        resetSocieteForm();
        alert('✅ Société créée avec succès !');
      } else {
        const errorData = await response.json();
        console.error('❌ Erreur API:', errorData);
        
        // Formater les erreurs de manière lisible
        let errorMessage = 'Erreur lors de la création:\n';
        if (errorData.detail && Array.isArray(errorData.detail)) {
          errorData.detail.forEach(err => {
            const field = err.loc ? err.loc.join(' > ') : 'Champ inconnu';
            errorMessage += `\n• ${field}: ${err.msg}`;
          });
        } else {
          errorMessage += errorData.detail || 'Erreur inconnue';
        }
        
        alert(`❌ ${errorMessage}`);
      }
    } catch (error) {
      console.error('❌ Error creating societe:', error);
      alert(`❌ Erreur: ${error.message}`);
    }
  };

  const handleSocieteSelection = (societe) => {
    setFormData({
      ...formData,
      client_id: societe.id,
      client_name: societe.name,
      commercial_responsable: societe.responsable_compte_nom || ''
    });
  };

  const handleViewOpportunity = (opportunity) => {
    setSelectedOpportunity(opportunity);
    setShowViewModal(true);
  };

  const handleViewOpportunityHistory = (opportunity) => {
    setSelectedOpportunity(opportunity);
    setShowHistoryModal(true);
  };

  const handleAIRelance = (opportunity) => {
    setSelectedOpportunity(opportunity);
    setShowAIRelanceModal(true);
  };

  // Selection multiple functions
  const handleSelectOpportunity = (opportunity) => {
    setSelectedOpportunities(prev => {
      if (prev.find(op => op.id === opportunity.id)) {
        return prev.filter(op => op.id !== opportunity.id);
      } else {
        return [...prev, opportunity];
      }
    });
  };

  const handleSelectAllOpportunities = (checked) => {
    if (checked) {
      setSelectedOpportunities(filteredAndSortedOpportunities());
    } else {
      setSelectedOpportunities([]);
    }
  };

  const handleBatchDelete = async () => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedOpportunities.length} opportunité(s) ?`)) {
      try {
        const deletePromises = selectedOpportunities.map(opp => 
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/opportunities/${opp.id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          })
        );
        
        await Promise.all(deletePromises);
        alert(`${selectedOpportunities.length} opportunité(s) supprimée(s) avec succès !`);
        setSelectedOpportunities([]);
        setShowBatchActionsModal(false);
        fetchOpportunities();
      } catch (error) {
        console.error('Error deleting opportunities:', error);
        alert('Erreur lors de la suppression des opportunités');
      }
    }
  };

  const handleBatchArchive = async () => {
    if (window.confirm(`Êtes-vous sûr de vouloir archiver ${selectedOpportunities.length} opportunité(s) ?`)) {
      try {
        const archivePromises = selectedOpportunities.map(opp => 
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/opportunities/${opp.id}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ statut: 'archivée' })
          })
        );
        
        await Promise.all(archivePromises);
        alert(`${selectedOpportunities.length} opportunité(s) archivée(s) avec succès !`);
        setSelectedOpportunities([]);
        setShowBatchActionsModal(false);
        fetchOpportunities();
      } catch (error) {
        console.error('Error archiving opportunities:', error);
        alert('Erreur lors de l\'archivage des opportunités');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      numero_opportunite: '',
      nom_opportunite: '',
      description: '',
      client_id: '',
      client_name: '',
      commercial_responsable: '',
      statut: 'prospection',
      valeur_estimee: 0,
      probabilite: 50,
      date_cloture_prevue: ''
    });
    setIsEditing(false);
    setSelectedOpportunity(null);
  };

  const resetSocieteForm = () => {
    setSocieteFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      rccm: '',
      contact_person: '',
      industry: '',
      status: 'active',
      notes: '',
      contact_facturation: {
        nom: '',
        email: '',
        telephone: ''
      },
      contact_livraison: {
        nom: '',
        email: '',
        telephone: '',
        adresse: ''
      },
      contact_notification: {
        nom: '',
        email: '',
        telephone: ''
      }
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'prospection': { 
        css: 'baobab-status-badge baobab-status-prospection', 
        text: 'Prospection',
        icon: '🔍'
      },
      'qualification': { 
        css: 'baobab-status-badge baobab-status-qualification', 
        text: 'Qualification',
        icon: '📋'
      },
      'proposition': { 
        css: 'baobab-status-badge baobab-status-proposition', 
        text: 'Proposition',
        icon: '💼'
      },
      'négociation': { 
        css: 'baobab-status-badge baobab-status-negotiation', 
        text: 'Négociation',
        icon: '🤝'
      },
      'gagnée': { 
        css: 'baobab-status-badge baobab-status-gagnee', 
        text: 'Gagnée',
        icon: '🎉'
      },
      'perdue': { 
        css: 'baobab-status-badge baobab-status-perdue', 
        text: 'Perdue',
        icon: '❌'
      },
      'archivée': { 
        css: 'baobab-status-badge bg-gray-100 text-gray-800', 
        text: 'Archivée',
        icon: '📦'
      }
    };
    
    const config = statusConfig[status] || statusConfig['prospection'];
    return (
      <span className={config.css}>
        <span className="mr-1">{config.icon}</span>
        {config.text}
      </span>
    );
  };

  const filteredAndSortedOpportunities = () => {
    let filtered = opportunities.filter(opportunity => {
      const matchesSearch = 
        opportunity.nom_opportunite.toLowerCase().includes(searchTerm.toLowerCase()) ||
        opportunity.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        opportunity.numero_opportunite.toLowerCase().includes(searchTerm.toLowerCase()) ||
        opportunity.description.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === '' || opportunity.statut === statusFilter;
      const matchesCommercial = commercialFilter === '' || opportunity.commercial_responsable.includes(commercialFilter);
      
      return matchesSearch && matchesStatus && matchesCommercial;
    });

    // Sort the filtered results
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      // Handle dates
      if (sortBy === 'date_creation' || sortBy === 'date_cloture_prevue' || sortBy === 'date_derniere_mise_a_jour') {
        aValue = new Date(aValue || a.updated_at);
        bValue = new Date(bValue || b.updated_at);
      }
      
      // Handle numbers
      if (sortBy === 'valeur_estimee' || sortBy === 'probabilite') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }
      
      // Handle strings
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="baobab-app">
        <div className="baobab-loading">
          <div className="text-center">
            <div className="baobab-spinner mx-auto mb-4"></div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Chargement des opportunités</h3>
            <p className="baobab-loading-text">Préparation de vos données commerciales...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="baobab-app">
      {/* Header amélioré et responsive */}
      <div className="baobab-header">
        <div className="baobab-header-content">
          <div className="baobab-header-main">
            {/* Titre et statistiques */}
            <div className="baobab-title-section">
              <div className="baobab-fade-in">
                <h1 className="baobab-title">Opportunités Commerciales</h1>
              </div>
            </div>

            {/* Actions et contrôles - version compacte */}
            <div className="baobab-actions flex items-center gap-2">
              {/* Contrôles de vue - version compacte */}
              <div className="flex bg-gray-100 rounded-md p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
                  title="Vue grille"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
                  title="Vue liste"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              
              {/* Bouton Export compact */}
              <button
                onClick={handleExportCSV}
                disabled={exportLoading}
                className={`p-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors ${exportLoading ? 'opacity-50' : ''}`}
                title="Exporter en CSV"
              >
                {exportLoading ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                )}
              </button>

              {/* Bouton Nouvelle Opportunité compact */}
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
              >
                <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span className="hidden sm:inline">Nouvelle</span>
                <span className="sm:hidden">+</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="px-4 sm:px-6 py-4">

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 mb-4">
        <button
          onClick={() => setActiveTab('opportunities')}
          className={`px-6 py-3 font-medium text-sm border-b-2 ${
            activeTab === 'opportunities'
              ? 'border-green-500 text-green-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          📊 Opportunités
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-6 py-3 font-medium text-sm border-b-2 ${
            activeTab === 'history'
              ? 'border-green-500 text-green-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          📋 Historique
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'opportunities' && (
        <div>
          {/* Search and Filters */}
          <div className="mb-6 p-4 bg-white rounded-lg shadow-md">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rechercher
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Nom, client, numéro, description..."
              className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Statut
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
            >
              <option value="">Tous les statuts</option>
              <option value="prospection">Prospection</option>
              <option value="qualification">Qualification</option>
              <option value="proposition">Proposition</option>
              <option value="négociation">Négociation</option>
              <option value="gagnée">Gagnée</option>
              <option value="perdue">Perdue</option>
              <option value="archivée">Archivée</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Commercial
            </label>
            <select
              value={commercialFilter}
              onChange={(e) => setCommercialFilter(e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
            >
              <option value="">Tous les commerciaux</option>
              {[...new Set(opportunities.map(opp => opp.commercial_responsable))].map(commercial => (
                <option key={commercial} value={commercial}>
                  {commercial}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Trier par
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
            >
              <option value="date_creation">Date de création</option>
              <option value="nom_opportunite">Nom</option>
              <option value="client_name">Client</option>
              <option value="valeur_estimee">Valeur</option>
              <option value="probabilite">Probabilité</option>
              <option value="date_cloture_prevue">Date de clôture</option>
            </select>
          </div>
        </div>
        <div className="mt-4 flex justify-between items-center">
          <p className="text-sm text-gray-600">
            {filteredAndSortedOpportunities().length} opportunité(s) trouvée(s)
          </p>
          <button
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('');
              setCommercialFilter('');
              setSortBy('date_creation');
              setSortOrder('desc');
            }}
            className="text-green-600 hover:text-green-800 text-sm"
          >
            Réinitialiser les filtres
          </button>
        </div>
      </div>

      {/* Batch Actions Bar */}
      {selectedOpportunities.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-700">
                {selectedOpportunities.length} opportunité(s) sélectionnée(s)
              </span>
              <button
                onClick={() => setSelectedOpportunities([])}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Désélectionner tout
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleBatchArchive}
                className="px-3 py-1 text-sm bg-yellow-500 text-white rounded hover:bg-yellow-600"
              >
                📦 Archiver
              </button>
              <button
                onClick={handleBatchDelete}
                className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600"
              >
                🗑️ Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Opportunities Display */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAndSortedOpportunities().slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((opportunity) => (
            <div
              key={opportunity.id}
              className="baobab-card cursor-pointer relative"
              onClick={() => setSelectedOpportunity(opportunity)}
            >
              {/* Checkbox for selection */}
              <div className="absolute top-2 left-2 z-10">
                <input
                  type="checkbox"
                  checked={selectedOpportunities.some(op => op.id === opportunity.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleSelectOpportunity(opportunity);
                  }}
                  className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                />
              </div>
              {/* Header avec statut et date */}
              <div className="baobab-card-header">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 truncate group-hover:text-baobab-primary transition-colors">
                      {opportunity.nom_opportunite}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Il y a {Math.floor((new Date() - new Date(opportunity.date_creation)) / (1000 * 60 * 60 * 24))} jours
                    </p>
                  </div>
                  {getStatusBadge(opportunity.statut)}
                </div>

                {/* Informations principales sans icônes */}
                <div className="space-y-2">
                  <div className="baobab-info-row">
                    <span className="baobab-info-label">
                      N° Opportunité:
                    </span>
                    <span className="baobab-info-value">{opportunity.numero_opportunite}</span>
                  </div>

                  <div className="baobab-info-row">
                    <span className="baobab-info-label">
                      Client:
                    </span>
                    <span className="baobab-info-value truncate ml-2 max-w-[180px]" title={opportunity.client_name}>
                      {opportunity.client_name}
                    </span>
                  </div>

                  <div className="baobab-info-row">
                    <span className="baobab-info-label">
                      Commercial:
                    </span>
                    <span className="baobab-info-value truncate ml-2 max-w-[180px]" title={opportunity.commercial_responsable}>
                      {opportunity.commercial_responsable.split('(')[0].trim()}
                    </span>
                  </div>

                  {/* Valeur et probabilité avec barres de progression */}
                  <div className="pt-1 border-t border-gray-100">
                    <div className="baobab-info-row mb-1">
                      <span className="baobab-info-label">
                        Valeur:
                      </span>
                      <span className="font-bold text-gray-900 text-base">
                        {new Intl.NumberFormat('fr-FR').format(opportunity.valeur_estimee)} FCFA
                      </span>
                    </div>

                    <div className="baobab-info-row">
                      <span className="baobab-info-label">
                        Probabilité:
                      </span>
                      <div className="flex items-center gap-2">
                        <div className="baobab-progress w-16">
                          <div 
                            className={`baobab-progress-fill ${
                              opportunity.probabilite >= 80 ? 'bg-green-500' :
                              opportunity.probabilite >= 60 ? 'bg-yellow-500' :
                              opportunity.probabilite >= 40 ? 'bg-orange-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${opportunity.probabilite}%` }}
                          ></div>
                        </div>
                        <span className="font-bold text-gray-900 text-sm">{opportunity.probabilite}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer avec actions */}
              <div className="baobab-card-footer">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    Créé le {new Date(opportunity.date_creation).toLocaleDateString('fr-FR')}
                  </span>
                  
                  <div className="flex items-center gap-2">
                    {/* Boutons d'action modernisés avec icônes */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewOpportunity(opportunity);
                      }}
                      className="baobab-action-button text-gray-600 hover:text-baobab-primary"
                      title="Voir les détails"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewOpportunityHistory(opportunity);
                      }}
                      className="baobab-action-button text-gray-600 hover:text-purple-600"
                      title="Voir l'historique"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditOpportunity(opportunity);
                      }}
                      className="baobab-action-button text-gray-600 hover:text-blue-600"
                      title="Éditer"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDocuments(opportunity);
                      }}
                      className="baobab-action-button text-gray-600 hover:text-green-600"
                      title="Documents"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </button>

                    {/* Menu des actions additionnelles */}
                    <div className="relative group/menu">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveDropdown(activeDropdown === opportunity.id ? null : opportunity.id);
                        }}
                        className="baobab-action-button text-gray-600 hover:text-gray-900"
                        title="Plus d'actions"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                        </svg>
                      </button>
                      
                      {/* Menu dropdown */}
                      <div className={`baobab-dropdown ${activeDropdown === opportunity.id ? 'active' : ''}`}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewOperations(opportunity);
                            setActiveDropdown(null);
                          }}
                          className="baobab-dropdown-item"
                        >
                          <span className="text-blue-500">🏭</span>
                          Opérations
                        </button>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAIRelance(opportunity);
                            setActiveDropdown(null);
                          }}
                          className="baobab-dropdown-item"
                        >
                          <span className="text-purple-500">🤖</span>
                          AI Relance
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDuplicateOpportunity(opportunity);
                            setActiveDropdown(null);
                          }}
                          className="baobab-dropdown-item"
                        >
                          <span className="text-blue-500">📋</span>
                          Dupliquer
                        </button>

                        {opportunity.statut !== 'archivée' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleArchiveOpportunity(opportunity);
                              setActiveDropdown(null);
                            }}
                            className="baobab-dropdown-item"
                          >
                            <span className="text-yellow-500">📦</span>
                            Archiver
                          </button>
                        )}

                        <hr className="my-1" />
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteOpportunity(opportunity);
                            setActiveDropdown(null);
                          }}
                          className="baobab-dropdown-item danger"
                        >
                          <span className="text-red-500">🗑️</span>
                          Supprimer
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectedOpportunities.length === filteredAndSortedOpportunities().length && filteredAndSortedOpportunities().length > 0}
                      onChange={(e) => handleSelectAllOpportunities(e.target.checked)}
                      className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                    />
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('numero_opportunite')}
                  >
                    N° Opportunité {getSortIcon('numero_opportunite')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('nom_opportunite')}
                  >
                    Nom {getSortIcon('nom_opportunite')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('client_name')}
                  >
                    Client {getSortIcon('client_name')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('statut')}
                  >
                    Statut {getSortIcon('statut')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('valeur_estimee')}
                  >
                    Valeur {getSortIcon('valeur_estimee')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('probabilite')}
                  >
                    Probabilité {getSortIcon('probabilite')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('date_creation')}
                  >
                    Date création {getSortIcon('date_creation')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('date_derniere_mise_a_jour')}
                  >
                    Dernière MAJ {getSortIcon('date_derniere_mise_a_jour')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAndSortedOpportunities().slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((opportunity) => (
                  <tr key={opportunity.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedOpportunities.some(op => op.id === opportunity.id)}
                        onChange={() => handleSelectOpportunity(opportunity)}
                        className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {opportunity.numero_opportunite}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {opportunity.nom_opportunite}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {opportunity.client_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {getStatusBadge(opportunity.statut)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(opportunity.valeur_estimee)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {opportunity.probabilite}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(opportunity.date_creation).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(opportunity.date_derniere_mise_a_jour || opportunity.updated_at).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleViewOpportunity(opportunity)}
                          className="text-gray-600 hover:text-gray-800 text-sm"
                        >
                          Voir
                        </button>
                        <button
                          onClick={() => handleViewOpportunityHistory(opportunity)}
                          className="text-purple-600 hover:text-purple-800 p-1 rounded hover:bg-purple-50 transition-colors"
                          title="Historique"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleAIRelance(opportunity)}
                          className="text-indigo-600 hover:text-indigo-800 p-1 rounded hover:bg-indigo-50 transition-colors"
                          title="AI Relance"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleViewOperations(opportunity)}
                          className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50 transition-colors"
                          title="Opérations"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleViewDocuments(opportunity)}
                          className="text-teal-600 hover:text-teal-800 p-1 rounded hover:bg-teal-50 transition-colors"
                          title="Documents"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleEditOpportunity(opportunity)}
                          className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50 transition-colors"
                          title="Éditer"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        {opportunity.statut !== 'archivée' && (
                          <button
                            onClick={() => handleArchiveOpportunity(opportunity)}
                            className="text-yellow-600 hover:text-yellow-800 p-1 rounded hover:bg-yellow-50 transition-colors"
                            title="Archiver"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteOpportunity(opportunity)}
                          className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 transition-colors"
                          title="Supprimer"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredAndSortedOpportunities().length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">Aucune opportunité trouvée</p>
              <p className="text-gray-400 text-sm mt-2">Essayez de modifier vos filtres ou créez une nouvelle opportunité</p>
            </div>
          )}

          {/* Pagination */}
          {filteredAndSortedOpportunities().length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(filteredAndSortedOpportunities().length / itemsPerPage)}
              totalItems={filteredAndSortedOpportunities().length}
              itemsPerPage={itemsPerPage}
              onPageChange={(page) => setCurrentPage(page)}
              onItemsPerPageChange={(newItemsPerPage) => {
                setItemsPerPage(newItemsPerPage);
                setCurrentPage(1);
              }}
            />
          )}
        </div>
      )}

      {/* Create Opportunity Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
          <div className="compact-modal bg-white rounded-lg w-full max-w-2xl overflow-y-auto">
            <h2>
              {isEditing ? 'Modifier l\'opportunité' : 'Nouvelle Opportunité'}
            </h2>
            
            <form onSubmit={isEditing ? handleUpdateOpportunity : handleCreateOpportunity} className="compact-form">
              <div className="compact-grid-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    N° Opportunité
                  </label>
                  <input
                    type="text"
                    value={formData.numero_opportunite}
                    onChange={(e) => setFormData({...formData, numero_opportunite: e.target.value})}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                    placeholder="Laissez vide pour génération automatique"
                    disabled={isEditing}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Société *
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={formData.client_id}
                      onChange={(e) => {
                        const selectedSociete = societes.find(s => s.id === e.target.value);
                        if (selectedSociete) {
                          handleSocieteSelection(selectedSociete);
                        }
                      }}
                      className="flex-1 px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      required
                    >
                      <option value="">
                        {societes.length === 0 ? 'Chargement des sociétés...' : 'Sélectionner une société'}
                      </option>
                      {societes.map(societe => (
                        <option key={societe.id} value={societe.id}>
                          {societe.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowCreateSocieteModal(true)}
                      className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                    >
                      + Nouvelle
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom de l'opportunité *
                  </label>
                  <input
                    type="text"
                    value={formData.nom_opportunite}
                    onChange={(e) => setFormData({...formData, nom_opportunite: e.target.value})}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Commercial responsable *
                  </label>
                  <input
                    type="text"
                    value={formData.commercial_responsable}
                    onChange={(e) => setFormData({...formData, commercial_responsable: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md bg-gray-50"
                    required
                    placeholder="Commercial responsable sera automatiquement assigné"
                    readOnly={!isEditing}
                  />
                  {!isEditing && (
                    <p className="text-sm text-gray-600 mt-1">
                      Automatiquement assigné à l'utilisateur connecté
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Statut
                  </label>
                  <select
                    value={formData.statut}
                    onChange={(e) => setFormData({...formData, statut: e.target.value})}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="prospection">Prospection</option>
                    <option value="qualification">Qualification</option>
                    <option value="proposition">Proposition</option>
                    <option value="négociation">Négociation</option>
                    <option value="gagnée">Gagnée</option>
                    <option value="perdue">Perdue</option>
                    {isEditing && <option value="archivée">Archivée</option>}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valeur estimée (XOF)
                  </label>
                  <input
                    type="number"
                    value={formData.valeur_estimee}
                    onChange={(e) => setFormData({...formData, valeur_estimee: parseFloat(e.target.value) || 0})}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                    min="0"
                    step="1000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Probabilité (%)
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={formData.probabilite}
                    onChange={(e) => setFormData({...formData, probabilite: parseInt(e.target.value)})}
                    className="w-full"
                  />
                  <div className="text-center text-sm text-gray-600">{formData.probabilite}%</div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de clôture prévue
                  </label>
                  <input
                    type="date"
                    value={formData.date_cloture_prevue}
                    onChange={(e) => setFormData({...formData, date_cloture_prevue: e.target.value})}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                    rows="3"
                    placeholder="Description de l'opportunité..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  {isEditing ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Societe Modal */}
      {showCreateSocieteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">🏢 Nouvelle Société</h2>
            
            {/* Note d'aide */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-800">
                <span className="font-semibold">ℹ️ Information :</span> Seul le <span className="font-bold">Nom de la société *</span> est obligatoire. 
                Tous les autres champs sont optionnels et peuvent être complétés plus tard.
              </p>
            </div>
            
            <form onSubmit={handleCreateSociete}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Informations de base */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-800">Informations de base</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom de la société *
                    </label>
                    <input
                      type="text"
                      value={societeFormData.name}
                      onChange={(e) => setSocieteFormData({...societeFormData, name: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                      <span className="text-gray-400 text-xs ml-1">(optionnel)</span>
                    </label>
                    <input
                      type="email"
                      value={societeFormData.email}
                      onChange={(e) => setSocieteFormData({...societeFormData, email: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      placeholder="contact@societe.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Téléphone
                      <span className="text-gray-400 text-xs ml-1">(optionnel)</span>
                    </label>
                    <input
                      type="tel"
                      value={societeFormData.phone}
                      onChange={(e) => setSocieteFormData({...societeFormData, phone: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      placeholder="05 00 00 00 00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Adresse
                      <span className="text-gray-400 text-xs ml-1">(optionnel)</span>
                    </label>
                    <textarea
                      value={societeFormData.address}
                      onChange={(e) => setSocieteFormData({...societeFormData, address: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      rows="3"
                      placeholder="20 BP 506 Abidjan, Côte d'Ivoire"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      RCCM
                      <span className="text-gray-400 text-xs ml-1">(optionnel)</span>
                    </label>
                    <input
                      type="text"
                      value={societeFormData.rccm}
                      onChange={(e) => setSocieteFormData({...societeFormData, rccm: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      placeholder="CI-ABJ-01-2023-B12-12345"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Personne de contact
                      <span className="text-gray-400 text-xs ml-1">(optionnel)</span>
                    </label>
                    <input
                      type="text"
                      value={societeFormData.contact_person}
                      onChange={(e) => setSocieteFormData({...societeFormData, contact_person: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      placeholder="Jean Dupont"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Secteur d'activité
                      <span className="text-gray-400 text-xs ml-1">(optionnel)</span>
                    </label>
                    <input
                      type="text"
                      value={societeFormData.industry}
                      onChange={(e) => setSocieteFormData({...societeFormData, industry: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      placeholder="Transport & Logistique"
                    />
                  </div>
                </div>

                {/* Contacts spécialisés */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-800">
                    Contacts spécialisés
                    <span className="text-gray-400 text-xs font-normal ml-2">(tous optionnels)</span>
                  </h3>
                  
                  {/* Contact Facturation */}
                  <div className="border rounded-md p-3">
                    <h4 className="font-medium text-sm text-gray-700 mb-2">
                      📄 Contact Facturation
                      <span className="text-gray-400 text-xs font-normal ml-1">(optionnel)</span>
                    </h4>
                    <div className="space-y-2">
                      <input
                        type="text"
                        placeholder="Nom"
                        value={societeFormData.contact_facturation.nom}
                        onChange={(e) => setSocieteFormData({
                          ...societeFormData,
                          contact_facturation: {
                            ...societeFormData.contact_facturation,
                            nom: e.target.value
                          }
                        })}
                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                      />
                      <input
                        type="email"
                        placeholder="Email"
                        value={societeFormData.contact_facturation.email}
                        onChange={(e) => setSocieteFormData({
                          ...societeFormData,
                          contact_facturation: {
                            ...societeFormData.contact_facturation,
                            email: e.target.value
                          }
                        })}
                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                      />
                      <input
                        type="tel"
                        placeholder="Téléphone"
                        value={societeFormData.contact_facturation.telephone}
                        onChange={(e) => setSocieteFormData({
                          ...societeFormData,
                          contact_facturation: {
                            ...societeFormData.contact_facturation,
                            telephone: e.target.value
                          }
                        })}
                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                  </div>

                  {/* Contact Livraison */}
                  <div className="border rounded-md p-3">
                    <h4 className="font-medium text-sm text-gray-700 mb-2">
                      🚚 Contact Livraison
                      <span className="text-gray-400 text-xs font-normal ml-1">(optionnel)</span>
                    </h4>
                    <div className="space-y-2">
                      <input
                        type="text"
                        placeholder="Nom"
                        value={societeFormData.contact_livraison.nom}
                        onChange={(e) => setSocieteFormData({
                          ...societeFormData,
                          contact_livraison: {
                            ...societeFormData.contact_livraison,
                            nom: e.target.value
                          }
                        })}
                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                      />
                      <input
                        type="email"
                        placeholder="Email"
                        value={societeFormData.contact_livraison.email}
                        onChange={(e) => setSocieteFormData({
                          ...societeFormData,
                          contact_livraison: {
                            ...societeFormData.contact_livraison,
                            email: e.target.value
                          }
                        })}
                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                      />
                      <input
                        type="tel"
                        placeholder="Téléphone"
                        value={societeFormData.contact_livraison.telephone}
                        onChange={(e) => setSocieteFormData({
                          ...societeFormData,
                          contact_livraison: {
                            ...societeFormData.contact_livraison,
                            telephone: e.target.value
                          }
                        })}
                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                      />
                      <input
                        type="text"
                        placeholder="Adresse de livraison"
                        value={societeFormData.contact_livraison.adresse}
                        onChange={(e) => setSocieteFormData({
                          ...societeFormData,
                          contact_livraison: {
                            ...societeFormData.contact_livraison,
                            adresse: e.target.value
                          }
                        })}
                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                  </div>

                  {/* Contact Notification */}
                  <div className="border rounded-md p-3">
                    <h4 className="font-medium text-sm text-gray-700 mb-2">
                      🔔 Contact Notification
                      <span className="text-gray-400 text-xs font-normal ml-1">(optionnel)</span>
                    </h4>
                    <div className="space-y-2">
                      <input
                        type="text"
                        placeholder="Nom"
                        value={societeFormData.contact_notification.nom}
                        onChange={(e) => setSocieteFormData({
                          ...societeFormData,
                          contact_notification: {
                            ...societeFormData.contact_notification,
                            nom: e.target.value
                          }
                        })}
                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                      />
                      <input
                        type="email"
                        placeholder="Email"
                        value={societeFormData.contact_notification.email}
                        onChange={(e) => setSocieteFormData({
                          ...societeFormData,
                          contact_notification: {
                            ...societeFormData.contact_notification,
                            email: e.target.value
                          }
                        })}
                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                      />
                      <input
                        type="tel"
                        placeholder="Téléphone"
                        value={societeFormData.contact_notification.telephone}
                        onChange={(e) => setSocieteFormData({
                          ...societeFormData,
                          contact_notification: {
                            ...societeFormData.contact_notification,
                            telephone: e.target.value
                          }
                        })}
                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={societeFormData.notes}
                    onChange={(e) => setSocieteFormData({...societeFormData, notes: e.target.value})}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                    rows="3"
                    placeholder="Notes sur la société..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateSocieteModal(false);
                    resetSocieteForm();
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  Créer la société
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedOpportunity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4 text-red-600">Confirmer la suppression</h2>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-2">
                Êtes-vous sûr de vouloir supprimer cette opportunité ?
              </p>
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="font-medium">{selectedOpportunity.nom_opportunite}</p>
                <p className="text-sm text-gray-600">N° {selectedOpportunity.numero_opportunite}</p>
                <p className="text-sm text-gray-600">Client: {selectedOpportunity.client_name}</p>
              </div>
              <p className="text-sm text-red-600 mt-2">
                Cette action est irréversible et supprimera définitivement l'opportunité.
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedOpportunity(null);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={confirmDeleteOpportunity}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Operations Modal */}
      {showOperationsModal && selectedOpportunity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                Opérations - {selectedOpportunity.nom_opportunite}
              </h2>
              <button
                onClick={() => setShowOperationsModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <span className="text-xl">×</span>
              </button>
            </div>
            
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-gray-800 mb-2">Détails de l'opportunité</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">N° Opportunité:</span>
                  <span className="font-medium ml-2">{selectedOpportunity.numero_opportunite}</span>
                </div>
                <div>
                  <span className="text-gray-600">Client:</span>
                  <span className="font-medium ml-2">{selectedOpportunity.client_name}</span>
                </div>
                <div>
                  <span className="text-gray-600">Statut:</span>
                  <span className="ml-2">{getStatusBadge(selectedOpportunity.statut)}</span>
                </div>
                <div>
                  <span className="text-gray-600">Valeur:</span>
                  <span className="font-medium ml-2">{formatCurrency(selectedOpportunity.valeur_estimee)}</span>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <h3 className="font-medium text-gray-800 mb-3">Opérations associées ({opportunityOperations.length})</h3>
              
              {opportunityOperations.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>Aucune opération associée à cette opportunité</p>
                  <button
                    onClick={() => {
                      setShowOperationsModal(false);
                      // Trigger navigation to operations module with create mode
                      window.dispatchEvent(new CustomEvent('navigateToOperations', {
                        detail: { 
                          create: true, 
                          opportunite_id: selectedOpportunity.id,
                          opportunite_nom: selectedOpportunity.nom_opportunite,
                          client: selectedOpportunity.client
                        }
                      }));
                    }}
                    className="mt-3 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Créer une opération
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {opportunityOperations.map((operation) => (
                    <div
                      key={operation.id}
                      className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => {
                        // Navigate to operation detail
                        setShowOperationsModal(false);
                        window.location.href = '/operations?view=' + operation.id;
                      }}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="font-medium text-gray-800">{operation.numero_operation}</h4>
                          <p className="text-sm text-gray-600">{operation.description}</p>
                        </div>
                        <div className="flex gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            operation.statut === 'planifiée' ? 'bg-blue-100 text-blue-800' :
                            operation.statut === 'en_cours' ? 'bg-yellow-100 text-yellow-800' :
                            operation.statut === 'terminée' ? 'bg-green-100 text-green-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {operation.statut === 'planifiée' ? 'Planifiée' :
                             operation.statut === 'en_cours' ? 'En cours' :
                             operation.statut === 'terminée' ? 'Terminée' :
                             'Annulée'}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            operation.priorite === 'Critique' ? 'bg-red-500 text-white' :
                            operation.priorite === 'Urgent' ? 'bg-red-100 text-red-800' :
                            operation.priorite === 'Élevée' ? 'bg-orange-100 text-orange-800' :
                            operation.priorite === 'Standard' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {operation.priorite}
                          </span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Type:</span>
                          <p className="font-medium">{operation.type_marchandise}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Itinéraire:</span>
                          <p className="font-medium">{operation.lieu_collecte} → {operation.lieu_livraison}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Valeur:</span>
                          <p className="font-medium">{formatCurrency(operation.valeur_estimee_xof)}</p>
                        </div>
                      </div>
                      
                      {operation.equipements_necessaires && operation.equipements_necessaires.length > 0 && (
                        <div className="mt-2">
                          <span className="text-xs text-gray-600">Équipements:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {operation.equipements_necessaires.slice(0, 3).map((eq, index) => (
                              <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                                {eq}
                              </span>
                            ))}
                            {operation.equipements_necessaires.length > 3 && (
                              <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                                +{operation.equipements_necessaires.length - 3} autres
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowOperationsModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Documents */}
      {showDocumentsModal && selectedOpportunity && (
        <OpportunityDocuments
          opportunityId={selectedOpportunity.id}
          opportunityName={selectedOpportunity.nom_opportunite}
          onClose={() => setShowDocumentsModal(false)}
        />
      )}
        </div>
      )}

      {/* View Opportunity Modal */}
      {showViewModal && selectedOpportunity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{zIndex: 10100}}>
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-800">
                Détails de l'opportunité - {selectedOpportunity.numero_opportunite}
              </h2>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Informations générales */}
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3">Informations générales</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">N° Opportunité:</span>
                      <span className="font-medium">{selectedOpportunity.numero_opportunite}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Nom:</span>
                      <span className="font-medium">{selectedOpportunity.nom_opportunite}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Client:</span>
                      <span className="font-medium">{selectedOpportunity.client_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Commercial:</span>
                      <span className="font-medium">{selectedOpportunity.commercial_responsable}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Statut:</span>
                      <span className="font-medium">{selectedOpportunity.statut}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Date création:</span>
                      <span className="font-medium">{new Date(selectedOpportunity.date_creation).toLocaleDateString('fr-FR')}</span>
                    </div>
                    {selectedOpportunity.date_cloture_prevue && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Date clôture prévue:</span>
                        <span className="font-medium">{new Date(selectedOpportunity.date_cloture_prevue).toLocaleDateString('fr-FR')}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                {selectedOpportunity.description && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">Description</h4>
                    <p className="text-sm text-gray-700 whitespace-pre-line">{selectedOpportunity.description}</p>
                  </div>
                )}
              </div>

              {/* Détails commerciaux */}
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3">Détails commerciaux</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Valeur estimée:</span>
                      <span className="font-medium text-green-600">{selectedOpportunity.valeur_estimee?.toLocaleString()} FCFA</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Probabilité:</span>
                      <span className="font-medium">{selectedOpportunity.probabilite}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Valeur pondérée:</span>
                      <span className="font-medium text-blue-600">
                        {Math.round((selectedOpportunity.valeur_estimee * selectedOpportunity.probabilite) / 100).toLocaleString()} FCFA
                      </span>
                    </div>
                  </div>
                </div>

                {/* Statistiques */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3">Statistiques</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Phase actuelle:</span>
                      <span className="font-medium capitalize">{selectedOpportunity.statut}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Dernière modification:</span>
                      <span className="font-medium">
                        {selectedOpportunity.date_modification 
                          ? new Date(selectedOpportunity.date_modification).toLocaleDateString('fr-FR')
                          : new Date(selectedOpportunity.date_creation).toLocaleDateString('fr-FR')
                        }
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Opportunity History Modal */}
      {showHistoryModal && selectedOpportunity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-800">
                Historique - {selectedOpportunity.numero_opportunite}
              </h2>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <ActivityTimeline 
              documentType="opportunite" 
              documentId={selectedOpportunity.id}
              showFilters={true}
              maxItems={50}
            />

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowHistoryModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Relance Modal */}
      {showAIRelanceModal && selectedOpportunity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-800">
                🤖 Génération AI Relance - {selectedOpportunity.numero_opportunite}
              </h2>
              <button
                onClick={() => setShowAIRelanceModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <AIRelanceGenerator
              documentId={selectedOpportunity.id}
              documentType="opportunite"
              currentStatus={selectedOpportunity.statut || 'qualification'}
              onRelanceGenerated={(relanceData) => {
                console.log('Relance generated for opportunity:', relanceData);
                // You can add additional logic here if needed
              }}
            />

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowAIRelanceModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Tab Content */}
      {activeTab === 'history' && (
        <div>
          <ActivityTimeline 
            documentType="opportunite" 
            showFilters={true}
            maxItems={50}
          />
        </div>
      )}
      </div>
    </div>
  );
};

export default OpportunitiesModule;